import React, { useMemo } from 'react';
import {
  CellTypes,
  DataGrid,
  DataGridProps,
  GridColDef,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import { Invoice } from '../../Lib/Api/vendor';

type Row = {
  id: string;
  invoice_number: CellTypes.DataGridCellCustomParams;
  status: CellTypes.DataGridCellBadgeParams;
  due_date: CellTypes.DataGridCellDateTimeParams;
  discount_date: CellTypes.DataGridCellDateTimeParams;
  po_number: CellTypes.DataGridCellCustomParams;
  total: CellTypes.DataGridCellStringParams;
};

const columns: GridColDef[] = [
  {
    field: 'invoice_number',
    headerName: 'Invoice Number',
    render: 'custom',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    render: 'badge',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'discount_date',
    headerName: 'Discount Date',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'po_number',
    headerName: 'PO',
    render: 'custom',
    display: 'flex',
    flex: 3,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'total',
    headerName: 'Total',
    display: 'flex',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

type Props = {
  isInfluencer: boolean;
  rows: Invoice[];
} & Omit<DataGridProps<Row>, 'columns'>;
function VendorsDataGrid({ rows, isInfluencer, ...dataGridProps }: Props) {
  const statusMapping: {
    [key: string]: 'primary' | 'green' | undefined;
  } = {
    'Paid In Full': 'green',
    Open: 'primary',
  };

  const transformedRows = useMemo(
    () =>
      rows.map(
        (invoice) =>
          ({
            id: invoice.id,
            invoice_number: (
              <Link
                to={`invoices/${invoice.internal_id}`}
                className="font-semibold underline text-primary-700"
              >
                {invoice.invoice_number}
              </Link>
            ),
            status: {
              children: invoice.status,
              contrast: 'low',
              color: statusMapping[invoice.status] ?? 'neutral',
            },
            due_date: {
              activity: false,
              time: false,
              date: invoice.due_date ?? '',
              timeZone: 'UTC',
            },
            discount_date: {
              activity: false,
              time: false,
              date: invoice.discount_date ?? '',
              timeZone: 'UTC',
            },
            po_number: (
              <Link
                to={`purchase-orders/${invoice.po_number}`}
                className="font-semibold underline text-primary-700"
              >
                {invoice.po_number}
              </Link>
            ),
            total: numeral(invoice.total).format('$0,0.00'),
          }) satisfies Row,
      ),
    [rows],
  );

  return (
    <DataGrid
      hideFilterButton
      hideGroupButton
      rows={transformedRows}
      paginationMode="server"
      sortingMode="server"
      initialState={{
        pinnedColumns: {
          left: ['invoice_number'],
        },
      }}
      {...dataGridProps}
      columns={
        isInfluencer
          ? columns.filter(
              (c) => c.field !== 'po_number' && c.field !== 'discount_date',
            )
          : columns
      }
    />
  );
}

export default VendorsDataGrid;
